import React, { useLayoutEffect, useRef } from "react";
import type { ReactNode } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faPlus,
} from "@fortawesome/pro-regular-svg-icons";
import { useStableDomId, useStableScopedDomId } from "../../useStableDomId";
import type { EmailBuilderSettingsSectionActivation } from "../settingsSectionActivation";
import { scrollEmailBuilderSettingsExpandedSectionIntoView } from "../emailBuilderSettingsPanelScroll";
import { useEmailBuilderSettingsAccordion } from "./EmailBuilderSettingsAccordionContext";

export type EmailBuilderSettingsAccordionSectionProps = {
  sectionId: string;
  title: string;
  headerActions?: ReactNode;
  children: ReactNode;
  className?: string;
  variant?: "default" | "nested";
  activation?: EmailBuilderSettingsSectionActivation;
};

export function EmailBuilderSettingsAccordionSection(
  props: EmailBuilderSettingsAccordionSectionProps,
): React.ReactElement {
  const {
    sectionId,
    title,
    headerActions,
    children,
    className,
    variant = "default",
    activation = { mode: "always" },
  } = props;
  const { t } = useTranslation();
  const { claimSectionDivider, expandedSectionIds, toggleSection, expandSection } =
    useEmailBuilderSettingsAccordion();
  const expanded = expandedSectionIds.has(sectionId);
  const showDivider = claimSectionDivider();
  const sectionRef = useRef<HTMLElement>(null);
  const prevExpandedRef = useRef(expanded);

  useLayoutEffect(() => {
    const wasExpanded = prevExpandedRef.current;
    prevExpandedRef.current = expanded;

    if (!expanded || wasExpanded || !sectionRef.current) {
      return;
    }

    scrollEmailBuilderSettingsExpandedSectionIntoView(sectionRef.current);
  }, [expanded]);

  const triggerId = useStableDomId("email-builder-settings-accordion-trigger");
  const panelId = useStableDomId("email-builder-settings-accordion-panel");
  const addTooltipId = useStableScopedDomId(
    "email-builder-settings-accordion-add",
    sectionId,
  );
  const isOptionalInactive = activation.mode === "optional" && !activation.isActive;
  const showAddIcon = isOptionalInactive && !expanded;
  const addLabel = t("Add {{section}}", { section: title.toLowerCase() });

  function applyOptionalAdd(): void {
    if (activation.mode !== "optional") {
      return;
    }
    activation.onAdd();
    expandSection(sectionId);
  }

  function handleHeaderActivate() {
    if (showAddIcon) {
      applyOptionalAdd();
      return;
    }
    toggleSection(sectionId);
  }

  const titleAriaLabel = showAddIcon ? addLabel : title;

  const toggleAriaLabel = showAddIcon
    ? addLabel
    : expanded
      ? t("Collapse {{section}}", { section: title })
      : t("Expand {{section}}", { section: title });

  const toggleButton = (
    <button
      type="button"
      className={classNames("email-builder-settings-accordion-section__toggle", {
        "email-builder-settings-accordion-section__toggle--add": showAddIcon,
      })}
      aria-expanded={expanded}
      aria-controls={panelId}
      aria-label={toggleAriaLabel}
      onClick={handleHeaderActivate}
    >
      <FontAwesomeIcon
        icon={showAddIcon ? faPlus : expanded ? faChevronUp : faChevronDown}
        aria-hidden="true"
      />
    </button>
  );

  return (
    <>
      {showDivider ? (
        <div className="email-builder-settings-accordion-divider" aria-hidden="true" />
      ) : null}
      <section
        ref={sectionRef}
        className={classNames("email-builder-settings-accordion-section", className, {
          "email-builder-settings-accordion-section--nested": variant === "nested",
          "email-builder-settings-accordion-section--inactive": isOptionalInactive,
        })}
        data-expanded={expanded ? "true" : "false"}
        data-active={activation.mode === "optional" && activation.isActive ? "true" : undefined}
      >
        <div className="email-builder-settings-accordion-section__header">
          <button
            type="button"
            id={triggerId}
            className="email-builder-settings-accordion-section__title-button"
            aria-expanded={expanded}
            aria-controls={panelId}
            aria-label={titleAriaLabel}
            onClick={handleHeaderActivate}
          >
            {title}
          </button>
          {headerActions ? (
            <div className="email-builder-settings-accordion-section__header-actions">{headerActions}</div>
          ) : null}
          {showAddIcon ? (
            <OverlayTrigger
              placement="left"
              delay={{ show: 400, hide: 100 }}
              overlay={<Tooltip id={addTooltipId}>{addLabel}</Tooltip>}
            >
              {toggleButton}
            </OverlayTrigger>
          ) : (
            toggleButton
          )}
        </div>
        <div
          id={panelId}
          role="region"
          aria-labelledby={triggerId}
          hidden={!expanded}
          className="email-builder-settings-accordion-section__panel"
        >
          <div className="email-builder-settings-accordion-section__content">{children}</div>
        </div>
      </section>
    </>
  );
}
